/**
 * Estoque Full — o que repor, o que está encalhado e o que vale migrar da coleta para o Full,
 * nas duas contas do Mercado Livre.
 *
 * Origem: o Artifact "Estoque Full Fiesta" (03/09/2026). As regras vieram inteiras de lá; o
 * dado agora é recoletado pelo motor em `FIESTA PARTY DADOS/scripts/estoque_full/`.
 *
 * Esta página só monta a tela. Filtro, ordenação, rótulo e formatação estão em
 * `estoqueFull/logic.ts`; o carregamento do dado em `estoqueFull/fonte.ts`; e as CONTAS de
 * negócio (cobertura, reposição, situação) no motor em Python, uma vez só.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { DomainPageShell } from '../components/DomainPageShell'
import { PropostaIaCard, type PropostaIaState } from '../components/PropostaIaCard'
import { usePanelInsightsFoco, useRegisterPanelInsights } from '../state/PanelInsightsContext'
import { pedirAnaliseProposta } from '../api/bridge'
import {
  carregarEstoqueFull,
  IDADE_ALERTA_DIAS,
  pedirColeta,
  POLL_MS,
  statusMotor,
  type DadosEstoqueFull,
  type StatusMotor,
} from './estoqueFull/fonte'
import {
  acaoSugerida,
  apelidoConta,
  brl,
  coresDoSku,
  dias,
  filtrarCandidatos,
  filtrarPerdas,
  filtrarSaiu,
  filtrarSkus,
  linkAnuncio,
  listaElegiveis,
  listaParado,
  listaQuaseLa,
  listaRepor,
  mlbsDoSku,
  num,
  pct,
  ressalvasMigracao,
  resumir,
  SITUACAO_ROTULO,
  SITUACAO_TOM,
  TODAS_AS_CONTAS,
  unidadesAEnviar,
  unidadesParadas,
} from './estoqueFull/logic'
import type { CandidatoFull, PerdaFull, SaiuDoFull, SkuFull, Urgencia } from './estoqueFull/types'
import { horaBrasilia, ROTULO_MARCA } from './margemAnuncio/logic'

// ---------------------------------------------------------------- tabela

type Coluna<T> = {
  label: string
  /** Chave de ordenação. Sem ela, a coluna não ordena (texto livre, ação sugerida). */
  ordenar?: (linha: T) => number | string | null
  alinhar?: 'left' | 'right'
  largura?: number | string
  render: (linha: T) => React.ReactNode
}

function TabelaOrdenavel<T>({
  colunas,
  linhas,
  vazio,
  ordemInicial,
}: {
  colunas: Array<Coluna<T>>
  linhas: T[]
  vazio: string
  ordemInicial?: number
}) {
  const [idx, setIdx] = useState<number | null>(ordemInicial ?? null)
  const [dir, setDir] = useState<'asc' | 'desc'>('desc')

  const ordenadas = useMemo(() => {
    if (idx == null || !colunas[idx]?.ordenar) return linhas
    const key = colunas[idx].ordenar!
    return [...linhas].sort((a, b) => {
      const va = key(a)
      const vb = key(b)
      if (typeof va === 'string' || typeof vb === 'string') {
        const sa = String(va ?? '')
        const sb = String(vb ?? '')
        return dir === 'asc' ? sa.localeCompare(sb, 'pt-BR') : sb.localeCompare(sa, 'pt-BR')
      }
      // nulo é "não informado": vai sempre para o fim, nas duas direções
      const na = va == null ? Number.NEGATIVE_INFINITY : va
      const nb = vb == null ? Number.NEGATIVE_INFINITY : vb
      return dir === 'asc' ? na - nb : nb - na
    })
  }, [colunas, linhas, idx, dir])

  if (!linhas.length) {
    return (
      <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">{vazio}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {colunas.map((c, i) => (
              <TableCell
                key={c.label}
                align={c.alinhar ?? 'left'}
                sx={{ whiteSpace: 'nowrap', width: c.largura, fontWeight: 600 }}
              >
                {c.ordenar ? (
                  <TableSortLabel
                    active={idx === i}
                    direction={idx === i ? dir : 'desc'}
                    onClick={() => {
                      if (idx === i) setDir(dir === 'desc' ? 'asc' : 'desc')
                      else {
                        setIdx(i)
                        setDir('desc')
                      }
                    }}
                  >
                    {c.label}
                  </TableSortLabel>
                ) : (
                  c.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ordenadas.map((linha, i) => (
            <TableRow key={i} hover>
              {colunas.map((c) => (
                <TableCell
                  key={c.label}
                  align={c.alinhar ?? 'left'}
                  sx={{ whiteSpace: c.largura ? 'normal' : 'nowrap' }}
                >
                  {c.render(linha)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

// ---------------------------------------------------------------- pedaços

function Produto({ s }: { s: SkuFull }) {
  const cores = coresDoSku(s)
  return (
    <Box sx={{ minWidth: 240, maxWidth: 360 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
        {s.titulo}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        <Box component="span" sx={{ fontFamily: 'monospace' }}>
          {s.inv}
        </Box>
        {mlbsDoSku(s).map((mlb) => (
          <span key={mlb}>
            {' · '}
            <Link href={linkAnuncio(mlb)} target="_blank" rel="noopener" underline="hover">
              {mlb}
            </Link>
          </span>
        ))}
        {cores.length > 0 && ` · ${cores.join(', ')}`}
      </Typography>
    </Box>
  )
}

const URGENCIA_ROTULO: Record<Urgencia, string> = {
  esta_semana: 'Enviar esta semana',
  proxima_semana: 'Enviar na próxima',
  duas_semanas: 'Em duas semanas',
  programar: 'Programar',
  nenhuma: '—',
}
const URGENCIA_TOM: Record<Urgencia, 'error' | 'warning' | 'info' | 'default'> = {
  esta_semana: 'error',
  proxima_semana: 'warning',
  duas_semanas: 'info',
  programar: 'default',
  nenhuma: 'default',
}

/**
 * Quando enviar. Não é a sugestão de reposição do Mercado Livre — ela só existe na tela
 * Estoque → Reposição do vendedor, não na API pública (dez rotas testadas em 02/09/2026).
 * Sai da cobertura da posição menos o trânsito até o CD: se o estoque acaba antes de uma
 * remessa enviada na semana que vem chegar, é "esta semana".
 */
function ChipUrgencia({ s, lead }: { s: SkuFull; lead: number }) {
  const u = s.urgencia ?? 'nenhuma'
  if (u === 'nenhuma') return <span>—</span>
  // repor > 0 implica venda no período (alvo = 45 × diária), então cobertura nunca é nula aqui.
  const dias = Math.round(s.cobertura ?? 0)
  const dica = `A posição (apto + a caminho) dura ${dias} dia(s); a remessa leva ~${lead} até o CD liberar.`
  return (
    <Tooltip title={dica}>
      <Chip size="small" label={URGENCIA_ROTULO[u]} color={URGENCIA_TOM[u]} variant={u === 'programar' ? 'outlined' : 'filled'} />
    </Tooltip>
  )
}

function ChipSituacao({ s }: { s: SkuFull }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Chip size="small" label={SITUACAO_ROTULO[s.situacao]} color={SITUACAO_TOM[s.situacao]} />
      {s.sem_estoque.length > 0 && (
        <Tooltip title="O próprio Mercado Livre marcou o anúncio como out_of_stock — a ruptura está confirmada na origem.">
          <Chip size="small" variant="outlined" label="ML confirma" />
        </Tooltip>
      )}
      {s.parado_hoje && s.transito > 0 && (
        <Tooltip title="Zero unidades aptas para venda agora. A situação já conta a remessa em rota, mas o anúncio só volta a vender quando ela entrar no CD.">
          <Chip size="small" color="warning" variant="outlined" label="parado até chegar" />
        </Tooltip>
      )}
    </Stack>
  )
}

type ComMargem = Pick<SkuFull, 'mc' | 'mc_pct' | 'mc_un' | 'mc_aj' | 'mc_aj_pct' | 'mc_aj_un' | 'ajuste_pis' | 'ajuste_inc' | 'difal_fantasma' | 'receita'>

/**
 * As DUAS margens da skill `margem-ajustada-fiesta`, sempre juntas: a AJUSTADA (com créditos)
 * em destaque — escolha da Mariana para este painel, 22/09/2026 — e a OFICIAL (notas fiscais)
 * logo abaixo, rotulada. O balão abre a memória: quanto foi PIS/COFINS e quanto foi o regime de
 * MG da Extrema (parcelas lidas da coleção `margens_ajustadas`). Sem a coleção para o anúncio a
 * ajustada fica "—" e a oficial aparece sozinha, dita.
 */
function DuasMargens({ m, campo }: { m: ComMargem; campo: 'pct' | 'un' }) {
  const aj = campo === 'pct' ? m.mc_aj_pct : m.mc_aj_un
  const of = campo === 'pct' ? m.mc_pct : m.mc_un
  const fmt = (v: number | null) => (campo === 'pct' ? pct(v) : brl(v, 2))
  const cor = (v: number | null) => (v != null && v < 0 ? 'error.main' : undefined)
  const memoria = (
    <Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
      <b>MC oficial (notas fiscais)</b>: {brl(m.mc)} · {pct(m.mc_pct)}
      {m.difal_fantasma ? (
        <>
          <br /><span style={{ opacity: 0.85 }}>(inclui {brl(m.difal_fantasma, 2)} de DIFAL fantasma devolvido)</span>
        </>
      ) : null}
      <br />+ ajuste de PIS/COFINS efetivo: {m.ajuste_pis == null ? 'não calculado' : brl(m.ajuste_pis, 2)}
      <br />+ regime de MG da Extrema (ICMS economizado − crédito perdido na transferência): {m.ajuste_inc == null ? '—' : brl(m.ajuste_inc, 2)}
      <br /><b>= MC ajustada (com créditos)</b>: {m.mc_aj == null ? 'sem a coleção margens_ajustadas para estes pedidos' : `${brl(m.mc_aj)} · ${pct(m.mc_aj_pct)}`}
      <br /><span style={{ opacity: 0.7 }}>Receita {brl(m.receita)} no período. Parcelas da coleção margens_ajustadas (skill margem-ajustada-fiesta).</span>
    </Box>
  )
  return (
    <Tooltip title={memoria} arrow placement="left">
      <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', cursor: 'help', lineHeight: 1.2 }}>
        <Box component="span" sx={{ fontWeight: 600, color: cor(aj) }}>{aj == null ? <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>—</Box> : fmt(aj)}</Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10.5 }}>oficial {fmt(of)}</Typography>
      </Box>
    </Tooltip>
  )
}

/** Margem no nível do anúncio (variações) aparece como "—", com o porquê a um toque. */
function Margem({ s, campo }: { s: SkuFull; campo: 'pct' | 'un' }) {
  if (s.mc_escopo === 'anuncio') {
    return (
      <Tooltip title="Este anúncio alimenta mais de um SKU (variações). `margens` guarda o total do pedido, não o valor por variação — então a margem fica no nível do anúncio e não é rateada aqui.">
        <Box component="span" sx={{ color: 'text.disabled' }}>
          —
        </Box>
      </Tooltip>
    )
  }
  return <DuasMargens m={s} campo={campo} />
}

function BarraComposicao({
  titulo,
  dica,
  partes,
}: {
  titulo: string
  dica: string
  partes: Array<{ rotulo: string; valor: number; cor: string }>
}) {
  const total = partes.reduce((a, p) => a + p.valor, 0) || 1
  const vis = partes.filter((p) => p.valor > 0)
  return (
    <Card variant="outlined" sx={{ flex: '1 1 320px' }}>
      <CardContent>
        <Typography variant="subtitle2">{titulo}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {dica}
        </Typography>
        <Box sx={{ display: 'flex', height: 26, borderRadius: 1, overflow: 'hidden', gap: '2px', bgcolor: 'action.hover' }}>
          {vis.map((p) => (
            <Box key={p.rotulo} sx={{ flex: p.valor, bgcolor: p.cor, minWidth: 2 }} title={`${p.rotulo}: ${num(p.valor)}`} />
          ))}
        </Box>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} sx={{ mt: 1.25 }}>
          {vis.map((p) => (
            <Stack key={p.rotulo} direction="row" spacing={0.75} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: '2.5px', bgcolor: p.cor }} />
              <Typography variant="caption" color="text.secondary">
                {p.rotulo} <b>{num(p.valor)}</b> ({Math.round((100 * p.valor) / total)}%)
              </Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

function CardAcao({
  tag,
  destaque,
  texto,
  cor,
  onClick,
}: {
  tag: string
  destaque: string
  texto: string
  cor: string
  onClick: () => void
}) {
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        flex: '1 1 250px',
        cursor: 'pointer',
        borderLeft: 3,
        borderLeftColor: cor,
        '&:hover': { boxShadow: 2 },
      }}
    >
      <CardContent>
        <Typography variant="overline" sx={{ color: cor, fontWeight: 600, letterSpacing: '.1em' }}>
          {tag}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
          {destaque}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.45 }}>
          {texto}
        </Typography>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------- página

export function EstoqueFullPage() {
  const theme = useTheme()
  const [estado, setEstado] = useState<DadosEstoqueFull | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [conta, setConta] = useState<string>(TODAS_AS_CONTAS)
  const [busca, setBusca] = useState('')
  const [aba, setAba] = useState(0)
  const [leituraIa, setLeituraIa] = useState<PropostaIaState>({ loading: false })
  const [motor, setMotor] = useState<StatusMotor | null>(null)
  const [pedindo, setPedindo] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)

  const carregar = useCallback((fresco = false) => {
    const ctrl = new AbortController()
    setCarregando(true)
    setErro(null)
    carregarEstoqueFull(ctrl.signal, fresco)
      .then((d) => setEstado(d))
      .catch((e: unknown) => {
        if ((e as Error)?.name === 'AbortError') return
        setErro(e instanceof Error ? e.message : 'Falha ao carregar o painel.')
      })
      .finally(() => setCarregando(false))
    return () => ctrl.abort()
  }, [])

  useEffect(() => carregar(), [carregar])

  // Estado do motor, para o botão saber se há coleta rodando. Só pergunta de novo enquanto
  // estiver coletando — parado, uma leitura por carga basta.
  useEffect(() => {
    let vivo = true
    let timer: number | undefined
    const perguntar = async () => {
      const s = await statusMotor()
      if (!vivo) return
      setMotor(s)
      if (s?.coletando) {
        timer = window.setTimeout(perguntar, POLL_MS)
      } else if (motor?.coletando && s && !s.coletando) {
        // acabou de terminar: recarrega com o retrato novo
        setRecado(
          s.ultima_coleta?.ok === false
            ? `A coleta falhou: ${s.ultima_coleta?.erro ?? 'motivo não informado'}`
            : 'Coleta do estoque concluída — a tela já está com o retrato novo.',
        )
        carregar(true)
      }
    }
    void perguntar()
    return () => {
      vivo = false
      if (timer) window.clearTimeout(timer)
    }
  }, [carregar, motor?.coletando])

  const atualizarEstoque = useCallback(async () => {
    setPedindo(true)
    const r = await pedirColeta()
    setRecado(r.mensagem)
    setPedindo(false)
    setMotor(await statusMotor())
  }, [])

  const dados = estado?.dados
  const skus = useMemo(
    () => (dados ? filtrarSkus(dados.skus, conta, busca) : []),
    [dados, conta, busca],
  )
  const candidatos = useMemo(
    () => (dados ? filtrarCandidatos(dados.candidatos, conta, busca) : []),
    [dados, conta, busca],
  )
  const perdas = useMemo(
    () => (dados ? filtrarPerdas(dados.meta.perdidos, conta, busca) : []),
    [dados, conta, busca],
  )
  // SKUs que venderam pelo CD e hoje não são Full (zeraram e saíram) — fora de `skus`, por isso
  // a lista própria. Ausente em payload anterior a 29/09/2026.
  const saiu = useMemo(
    () => (dados ? filtrarSaiu(dados.saiu_do_full ?? [], conta, busca) : []),
    [dados, conta, busca],
  )
  const unSaiu = useMemo(() => saiu.reduce((a, s) => a + s.un30, 0), [saiu])
  const r = useMemo(() => resumir(skus), [skus])
  const repor = useMemo(() => listaRepor(skus), [skus])
  const parado = useMemo(() => listaParado(skus), [skus])
  const elegiveis = useMemo(() => listaElegiveis(candidatos), [candidatos])
  // Quem da lista de reposição já tem mercadoria a caminho — o dado que evita mandar duas
  // vezes a mesma coisa.
  const emRota = useMemo(() => repor.filter((s) => s.transito > 0), [repor])
  const unidadesEmRota = useMemo(() => emRota.reduce((a, s) => a + s.transito, 0), [emRota])
  const semFaltar = useMemo(() => emRota.filter((s) => !s.repor).length, [emRota])
  const quaseLa = useMemo(
    () => (dados ? listaQuaseLa(candidatos, dados.cortes.unidades_mes) : []),
    [candidatos, dados],
  )

  const stats = dados
    ? [
        { label: 'SKUs no Full', value: num(r.skus), hint: `${num(r.unidades_cd)} unidades no CD` },
        { label: 'Disponível pra venda', value: num(r.disp), hint: 'prontas no CD agora' },
        { label: 'A caminho', value: num(r.transito), hint: 'enviadas, CD ainda não liberou' },
        {
          label: 'Vendeu em 30 dias',
          value: num(r.un30 + unSaiu),
          hint: `saíram do CD${unSaiu > 0 ? ` · ${num(unSaiu)} de ${saiu.length} SKUs que já saíram do Full` : ''} · +${num(r.un30_fora_cd)} por Flex/coleta`,
        },
        { label: 'Receita do Full', value: brl(r.receita), hint: `${dados.periodo.dias} dias` },
        {
          label: 'MC ajustada (com créditos)',
          value: pct(r.mc_aj_pct),
          hint:
            r.mc_aj_pct == null
              ? dados.meta.margem_ajustada?.aviso ?? 'sem a coleção margens_ajustadas no período'
              : `${brl(r.mc_aj)} · PIS/COFINS ${r.ajuste_pis >= 0 ? '+' : ''}${brl(r.ajuste_pis)}${r.ajuste_inc ? ` · regime ${r.ajuste_inc >= 0 ? '+' : ''}${brl(r.ajuste_inc)}` : ''}`,
        },
        {
          label: 'MC oficial (notas fiscais)',
          value: pct(r.mc_pct),
          hint: `${brl(r.mc)} no período`,
        },
        {
          label: 'Custo parado',
          value: brl(r.valor_parado),
          hint: `${num(unidadesParadas(skus))} unidades sem giro`,
        },
      ]
    : undefined

  const subtitulo = dados
    ? `Vendas que saíram do CD de ${dados.meta.vendas_fonte?.periodo.de ?? dados.periodo.de} a ${dados.meta.vendas_fonte?.periodo.ate ?? dados.periodo.ate} · estoque, vendas e visitas lidos na API do Mercado Livre em ${dados.gerado_em}`
    : 'carregando…'

  const { focoRef } = usePanelInsightsFoco()
  const carregarLeitura = useCallback(async () => {
    if (!dados || carregando || erro) return
    setLeituraIa((prev) => ({ ...prev, loading: true, erro: null }))
    const fallback = [
      `Full: ${num(r.skus)} SKUs · ${num(r.unidades_cd)} un no CD · ${num(r.disp)} disponíveis · ${num(r.transito)} a caminho.`,
      `Repor agora: ${num(repor.length)} SKUs (${num(unidadesAEnviar(skus))} un a enviar; ${num(r.ruptura)} zerados).`,
      `Zerou e saiu do Full: ${num(saiu.length)} SKUs · ${num(unSaiu)} un vendidas pelo CD antes de sair.`,
      `Encalhado: ${num(parado.length)} SKUs · ${num(unidadesParadas(skus))} un sem giro · custo ${brl(r.valor_parado)}.`,
      `Migrar pro Full: ${num(elegiveis.length)} anúncios elegíveis · quase lá ${num(quaseLa.length)}.`,
      `Receita ${brl(r.receita)} · MC oficial ${pct(r.mc_pct)} (${brl(r.mc)}) · MC ajustada ${pct(r.mc_aj_pct)} (${brl(r.mc_aj)}).`,
      `Retrato de ${dados.gerado_em} · período ${dados.periodo.de} a ${dados.periodo.ate} · alvo ${dados.alvo_dias} dias.`,
      'Verdade = KPIs e tabelas desta tela (JSON estático do motor estoque_full).',
    ].join('\n')
    const data = await pedirAnaliseProposta({
      tipo: 'insight_reflexao',
      insight: {
        dominio: 'estoque_full',
        dominio_label: 'Estoque Full',
        titulo: 'Leitura do estoque Full ML',
        leitura: fallback,
        ...(focoRef.current ? { foco_usuario: focoRef.current } : {}),
        periodo: `${dados.periodo.de} a ${dados.periodo.ate}`,
        metricas: {
          skus: r.skus,
          unidades_cd: r.unidades_cd,
          disponivel: r.disp,
          transito: r.transito,
          repor_skus: repor.length,
          unidades_a_enviar: unidadesAEnviar(skus),
          ruptura: r.ruptura,
          encalhado_skus: parado.length,
          unidades_paradas: unidadesParadas(skus),
          valor_parado: r.valor_parado,
          migrar_elegiveis: elegiveis.length,
          quase_la: quaseLa.length,
          receita: r.receita,
          mc_oficial: r.mc,
          mc_oficial_pct: r.mc_pct,
          mc_ajustada: r.mc_aj,
          mc_ajustada_pct: r.mc_aj_pct,
          alvo_dias: dados.alvo_dias,
        },
      },
    })
    if (!data.ok) {
      setLeituraIa({ loading: false, texto: fallback, fonte: 'regras', modelo: null, erro: null })
      return
    }
    setLeituraIa({
      loading: false,
      texto: data.leitura || data.texto || fallback,
      modelo: data.modelo,
      fonte: data.fonte,
      cached: data.cached,
      gerado_em: data.gerado_em,
    })
  }, [dados, carregando, erro, r, repor, parado, elegiveis, quaseLa, skus])

  useEffect(() => {
    if (!dados || carregando || erro) return
    void carregarLeitura()
  }, [dados, carregando, erro, conta, carregarLeitura])

  useRegisterPanelInsights({
    titulo: 'Estoque Full',
    verdade: 'Verdade = KPIs e tabelas desta tela (JSON do motor estoque_full). Proposta = leitura da IA — revise antes de agir.',
    estado: leituraIa,
    recarregar: carregarLeitura,
  })

  // ---- tabelas

  const colProduto: Coluna<SkuFull> = {
    label: 'Produto',
    largura: 340,
    ordenar: (s) => s.titulo,
    render: (s) => <Produto s={s} />,
  }
  const colConta: Coluna<SkuFull> = {
    label: 'Conta',
    ordenar: (s) => (dados ? apelidoConta(dados, s.conta) : s.conta),
    render: (s) => (
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {dados ? apelidoConta(dados, s.conta) : s.conta}
      </Typography>
    ),
  }

  const colsRepor: Array<Coluna<SkuFull>> = [
    colProduto,
    colConta,
    { label: 'Situação', ordenar: (s) => s.cobertura, render: (s) => <ChipSituacao s={s} /> },
    {
      label: 'Cobertura',
      alinhar: 'right',
      ordenar: (s) => s.cobertura,
      // A cobertura mostrada é a da posição (apto + a caminho), que é a que define a situação.
      // Quando ela difere da cobertura só do apto, as duas aparecem — senão a tela esconderia
      // que o anúncio pode estar parado agora, esperando a remessa.
      render: (s) => (
        <Stack spacing={0.25} alignItems="flex-end">
          <span>{dias(s.cobertura)}</span>
          {s.transito > 0 && s.cobertura_disp != null && s.cobertura_disp !== s.cobertura && (
            <Tooltip title="Cobertura contando só o que está apto para venda hoje. A de cima soma o que já está a caminho.">
              <Typography variant="caption" color="text.disabled">
                só apto: {dias(s.cobertura_disp)}
              </Typography>
            </Tooltip>
          )}
        </Stack>
      ),
    },
    { label: 'Disponível', alinhar: 'right', ordenar: (s) => s.disp, render: (s) => num(s.disp) },
    {
      label: 'A caminho',
      alinhar: 'right',
      ordenar: (s) => s.transito,
      render: (s) => (s.transito ? num(s.transito) : '—'),
    },
    {
      label: 'Posição',
      alinhar: 'right',
      ordenar: (s) => s.posicao,
      render: (s) => <b>{num(s.posicao)}</b>,
    },
    {
      label: 'Vendeu 30d',
      alinhar: 'right',
      ordenar: (s) => s.un30,
      // Só o que saiu do CD — é o número que bate com o painel do ML (29/09/2026). O que o
      // anúncio vendeu por Flex/coleta aparece embaixo, para não sumir da vista.
      render: (s) => (
        <Stack spacing={0} alignItems="flex-end">
          <span>{num(s.un30)}</span>
          {(s.un30_fora_cd ?? 0) > 0 && (
            <Tooltip title="Unidades vendidas pelos mesmos anúncios, mas enviadas por Flex ou coleta — não passaram pelo CD e não entram na cobertura.">
              <Typography variant="caption" color="text.secondary">+{num(s.un30_fora_cd ?? 0)} fora do CD</Typography>
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      label: 'Enviar',
      alinhar: 'right',
      ordenar: (s) => s.repor,
      // O número já é líquido do que está em rota. Quem decide o envio precisa VER isso na
      // célula, senão manda de novo o que já saiu — foi o pedido da Mariana em 25/09/2026.
      render: (s) => (
        <Stack spacing={0.25} alignItems="flex-end">
          {s.repor ? (
            <b>{num(s.repor)}</b>
          ) : (
            <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>não precisa</Box>
          )}
          {s.transito > 0 && (
            <Tooltip title={`${num(s.transito)} unidade(s) já saíram e o CD ainda não liberou. Este "Enviar" já é o que falta DEPOIS delas — mandar ${num(s.transito)} de novo seria estoque dobrado.`}>
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={`já vêm ${num(s.transito)}`}
                sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10.5 } }}
              />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      label: 'Quando enviar',
      ordenar: (s) => ['esta_semana', 'proxima_semana', 'duas_semanas', 'programar', 'nenhuma'].indexOf(s.urgencia ?? 'nenhuma'),
      render: (s) => <ChipUrgencia s={s} lead={dados?.lead_dias ?? 7} />,
    },
    { label: 'MC ajust. %', alinhar: 'right', ordenar: (s) => s.mc_aj_pct ?? s.mc_pct, render: (s) => <Margem s={s} campo="pct" /> },
    { label: 'MC ajust./un', alinhar: 'right', ordenar: (s) => s.mc_aj_un ?? s.mc_un, render: (s) => <Margem s={s} campo="un" /> },
  ]
  // "Quando enviar" também em Todos os SKUs: a barra de urgência conta SKUs saudáveis com
  // "programar", que não entram na aba Repor — sem esta coluna a pessoa não os acharia.
  const colQuandoEnviar: Coluna<SkuFull> = {
    label: 'Quando enviar',
    ordenar: (s) => ['esta_semana', 'proxima_semana', 'duas_semanas', 'programar', 'nenhuma'].indexOf(s.urgencia ?? 'nenhuma'),
    render: (s) => <ChipUrgencia s={s} lead={dados?.lead_dias ?? 7} />,
  }

  const colsParado: Array<Coluna<SkuFull>> = [
    colProduto,
    colConta,
    { label: 'Situação', ordenar: (s) => s.situacao, render: (s) => <ChipSituacao s={s} /> },
    { label: 'No CD', alinhar: 'right', ordenar: (s) => s.total, render: (s) => num(s.total) },
    { label: 'Disponível', alinhar: 'right', ordenar: (s) => s.disp, render: (s) => num(s.disp) },
    {
      // Pedido da Mariana em 29/09/2026: a remessa em rota item a item também aqui — SKU parado
      // com mais unidades chegando é o pior caso, e precisa aparecer antes de decidir retirar.
      label: 'A caminho',
      alinhar: 'right',
      ordenar: (s) => s.transito,
      render: (s) =>
        s.transito ? (
          <Tooltip title="Unidades já despachadas que o CD ainda não liberou. Vão se somar ao que já está parado.">
            <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>{num(s.transito)}</Box>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      label: 'Vendeu 30d',
      alinhar: 'right',
      ordenar: (s) => s.un30,
      render: (s) =>
        s.un30 ? num(s.un30) : <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>0</Box>,
    },
    { label: 'Visitas 30d', alinhar: 'right', ordenar: (s) => s.visitas30, render: (s) => num(s.visitas30) },
    { label: 'Preço', alinhar: 'right', ordenar: (s) => s.preco, render: (s) => brl(s.preco, 2) },
    // As duas margens também no encalhado (29/09/2026): SKU parado com MC boa é caso de
    // tráfego/anúncio; SKU parado com MC negativa não vale nem o esforço de vender.
    { label: 'MC ajust. %', alinhar: 'right', ordenar: (s) => s.mc_aj_pct ?? s.mc_pct, render: (s) => <Margem s={s} campo="pct" /> },
    { label: 'MC ajust./un', alinhar: 'right', ordenar: (s) => s.mc_aj_un ?? s.mc_un, render: (s) => <Margem s={s} campo="un" /> },
    {
      label: 'Custo parado',
      alinhar: 'right',
      ordenar: (s) => s.valor_parado,
      render: (s) =>
        s.valor_parado == null ? (
          <Tooltip title="Sem CMV para este SKU nem em margens nem em cmvs. Não existe produto com custo zero — enquanto o custo não for cadastrado, o valor imobilizado deste SKU não entra em nenhum total.">
            <Box component="span" sx={{ color: 'text.disabled' }}>custo não cadastrado</Box>
          </Tooltip>
        ) : (
          brl(s.valor_parado)
        ),
    },
    { label: 'Ação sugerida', largura: 300, render: (s) => (
      <Typography variant="body2" color="text.secondary">{acaoSugerida(s)}</Typography>
    ) },
  ]

  const colsMigrar: Array<Coluna<CandidatoFull>> = [
    {
      label: 'Anúncio',
      largura: 340,
      ordenar: (c) => c.titulo,
      render: (c) => (
        <Box sx={{ minWidth: 240, maxWidth: 360 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.35 }}>{c.titulo}</Typography>
          <Typography variant="caption" color="text.secondary">
            <Link href={linkAnuncio(c.mlb)} target="_blank" rel="noopener" underline="hover">
              {c.mlb}
            </Link>
            {` · ${c.logistica}`}
          </Typography>
        </Box>
      ),
    },
    {
      label: 'Conta',
      ordenar: (c) => (dados ? apelidoConta(dados, c.conta) : c.conta),
      render: (c) => (
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {dados ? apelidoConta(dados, c.conta) : c.conta}
        </Typography>
      ),
    },
    {
      label: 'Conversão',
      alinhar: 'right',
      ordenar: (c) => c.conv,
      render: (c) => (c.conv == null ? '—' : <b>{pct(c.conv)}</b>),
    },
    { label: 'Vendeu 30d', alinhar: 'right', ordenar: (c) => c.un30, render: (c) => num(c.un30) },
    { label: 'Visitas 30d', alinhar: 'right', ordenar: (c) => c.visitas30, render: (c) => num(c.visitas30) },
    { label: 'MC ajust. %', alinhar: 'right', ordenar: (c) => c.mc_aj_pct ?? c.mc_pct, render: (c) => <DuasMargens m={c} campo="pct" /> },
    { label: 'MC ajust./un', alinhar: 'right', ordenar: (c) => c.mc_aj_un ?? c.mc_un, render: (c) => <DuasMargens m={c} campo="un" /> },
    {
      label: 'Enviar p/ 45d',
      alinhar: 'right',
      ordenar: (c) => c.envio_45d,
      render: (c) => <b>{num(c.envio_45d)}</b>,
    },
    {
      label: 'Antes de enviar',
      largura: 300,
      render: (c) => {
        const rs = ressalvasMigracao(c)
        return (
          <Typography variant="body2" color={rs.length ? 'text.secondary' : 'success.main'}>
            {rs.length ? rs.join(' ') : 'Nada travando. Pode enviar.'}
          </Typography>
        )
      },
    },
  ]

  const colsSaiu: Array<Coluna<SaiuDoFull>> = [
    {
      label: 'Produto',
      largura: 340,
      ordenar: (s) => s.titulo,
      render: (s) => (
        <Box sx={{ minWidth: 240, maxWidth: 360 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.35 }}>{s.titulo}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>{s.sku ?? s.inv ?? '—'}</Box>
            {s.anuncios.map((mlb) => (
              <span key={mlb}>
                {' · '}
                <Link href={linkAnuncio(mlb)} target="_blank" rel="noopener" underline="hover">
                  {mlb}
                </Link>
              </span>
            ))}
          </Typography>
        </Box>
      ),
    },
    {
      label: 'Conta',
      ordenar: (s) => (dados ? apelidoConta(dados, s.conta) : s.conta),
      render: (s) => (
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {dados ? apelidoConta(dados, s.conta) : s.conta}
        </Typography>
      ),
    },
    {
      label: 'Hoje',
      ordenar: (s) => `${s.logistica} ${s.status}`,
      render: (s) => (
        <Typography variant="caption">
          {s.logistica}
          {s.status !== 'active' && ` · ${s.status}`}
        </Typography>
      ),
    },
    {
      label: 'Vendeu pelo CD',
      alinhar: 'right',
      ordenar: (s) => s.un30,
      render: (s) => (
        <>
          <b>{num(s.un30)}</b>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {num(s.ped30)} pedidos
          </Typography>
        </>
      ),
    },
    {
      label: 'Vende agora por coleta',
      alinhar: 'right',
      ordenar: (s) => s.un30_fora_cd,
      render: (s) => num(s.un30_fora_cd),
    },
    {
      label: 'Estoque no anúncio',
      alinhar: 'right',
      ordenar: (s) => s.estoque_anuncio,
      render: (s) => num(s.estoque_anuncio),
    },
    {
      label: `Enviar p/ ${dados?.alvo_dias ?? 45}d`,
      alinhar: 'right',
      ordenar: (s) => s.enviar,
      render: (s) => <b>{num(s.enviar)}</b>,
    },
  ]

  const colsPerdas: Array<Coluna<PerdaFull>> = [
    {
      label: 'Produto',
      largura: 340,
      ordenar: (p) => p.titulo,
      render: (p) => (
        <Box sx={{ minWidth: 240, maxWidth: 360 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.35 }}>{p.titulo}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {p.inv}
          </Typography>
        </Box>
      ),
    },
    {
      label: 'Conta',
      ordenar: (p) => (dados ? apelidoConta(dados, p.conta) : p.conta),
      render: (p) => (
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {dados ? apelidoConta(dados, p.conta) : p.conta}
        </Typography>
      ),
    },
    {
      label: 'Unidades',
      alinhar: 'right',
      ordenar: (p) => p.qtd,
      render: (p) => <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>{num(p.qtd)}</Box>,
    },
    {
      label: 'Perdido ou avariado',
      alinhar: 'right',
      ordenar: (p) => p.perdido,
      render: (p) => (p.perdido ? num(p.perdido) : '—'),
    },
    {
      label: 'Retido',
      alinhar: 'right',
      ordenar: (p) => p.bloqueado,
      render: (p) => (p.bloqueado ? num(p.bloqueado) : '—'),
    },
    {
      label: 'Motivo registrado pelo ML',
      ordenar: (p) => p.detalhe,
      render: (p) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }} color="text.secondary">
          {p.detalhe || '—'}
        </Typography>
      ),
    },
  ]

  const colsTodos: Array<Coluna<SkuFull>> = [
    colProduto,
    colConta,
    { label: 'Situação', ordenar: (s) => s.situacao, render: (s) => <ChipSituacao s={s} /> },
    { label: 'No CD', alinhar: 'right', ordenar: (s) => s.total, render: (s) => num(s.total) },
    { label: 'Disponível', alinhar: 'right', ordenar: (s) => s.disp, render: (s) => num(s.disp) },
    {
      label: 'A caminho',
      alinhar: 'right',
      ordenar: (s) => s.transito,
      render: (s) => (s.transito ? num(s.transito) : '—'),
    },
    {
      label: 'Indisponível',
      alinhar: 'right',
      ordenar: (s) => s.perdido + s.bloqueado,
      render: (s) => (s.perdido + s.bloqueado ? num(s.perdido + s.bloqueado) : '—'),
    },
    { label: 'Vendeu 30d', alinhar: 'right', ordenar: (s) => s.un30, render: (s) => num(s.un30) },
    { label: 'Visitas 30d', alinhar: 'right', ordenar: (s) => s.visitas30, render: (s) => num(s.visitas30) },
    { label: 'Conversão', alinhar: 'right', ordenar: (s) => s.conv, render: (s) => pct(s.conv) },
    { label: 'Cobertura', alinhar: 'right', ordenar: (s) => s.cobertura, render: (s) => dias(s.cobertura) },
    { label: 'Receita 30d', alinhar: 'right', ordenar: (s) => s.receita, render: (s) => brl(s.receita) },
    { label: 'MC ajust. %', alinhar: 'right', ordenar: (s) => s.mc_aj_pct ?? s.mc_pct, render: (s) => <Margem s={s} campo="pct" /> },
    colQuandoEnviar,
    {
      label: 'Enviar',
      alinhar: 'right',
      ordenar: (s) => s.repor,
      render: (s) => (s.repor ? num(s.repor) : '—'),
    },
  ]

  return (
    <DomainPageShell
      title="Estoque Full"
      subtitle={subtitulo}
      loading={carregando}
      error={erro}
      onRetry={carregar}
      stats={stats}
    >
      {dados && estado && (
        <Stack spacing={2.5}>
          <ProcedenciaFull
            estado={estado}
            motor={motor}
            pedindo={pedindo}
            onAtualizar={() => void atualizarEstoque()}
          />
          {recado && (
            <Alert severity={recado.startsWith('A coleta falhou') ? 'error' : 'success'} onClose={() => setRecado(null)}>
              {recado}
            </Alert>
          )}
          {estado.fonte === 'arquivo' && (
            <Alert severity="warning">
              O motor de atualização não respondeu{estado.motivoArquivo ? ` (${estado.motivoArquivo})` : ''} —
              esta tela está servindo o <b>arquivo publicado com o site</b>, de {dados.gerado_em}.
              Enquanto isso durar, o painel não acompanha a base: avise o time para conferir o
              serviço <Box component="code" sx={{ fontFamily: 'monospace' }}>estoque-full</Box>.
            </Alert>
          )}
          {estado.fonte === 'arquivo' && estado.idadeDias >= IDADE_ALERTA_DIAS && (
            <Alert severity="warning">
              E esse arquivo é um retrato de <b>{dados.gerado_em}</b> — {estado.idadeDias} dias
              atrás. Estoque do Full muda todo dia: não decida reposição por ele sem atualizar.
            </Alert>
          )}
          {estado.avisos.map((a) => (
            <Alert key={a} severity="info">
              {a}
            </Alert>
          ))}
          {dados.meta.margem_ajustada?.aviso && (
            <Alert severity="warning">
              MC ajustada indisponível: {dados.meta.margem_ajustada.aviso}. As colunas de margem
              mostram só a <b>MC oficial (notas fiscais)</b> até a coleção ser carregada.
            </Alert>
          )}

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={conta}
              onChange={(_, v) => v && setConta(v)}
            >
              <ToggleButton value={TODAS_AS_CONTAS}>As duas contas</ToggleButton>
              {Object.entries(dados.contas).map(([id, c]) => (
                <ToggleButton key={id} value={id}>
                  {c.apelido}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <TextField
              size="small"
              placeholder="Buscar produto, SKU ou MLB"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Typography variant="caption" color="text.secondary">
              {num(skus.length)} SKUs no filtro
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <CardAcao
              tag="Repor agora"
              destaque={`${num(repor.length)} SKUs`}
              texto={`${num(unidadesAEnviar(skus))} unidades a enviar para fechar ${dados.alvo_dias} dias de cobertura. ${num(r.ruptura)} já estão zerados no CD.`}
              cor={theme.palette.error.main}
              onClick={() => setAba(0)}
            />
            <CardAcao
              tag="Encalhado"
              destaque={`${num(parado.length)} SKUs`}
              texto={`${num(unidadesParadas(skus))} unidades sem giro no CD, ${brl(r.valor_parado)} de custo identificado pagando armazenagem.`}
              cor={theme.palette.secondary.main}
              onClick={() => setAba(1)}
            />
            <CardAcao
              tag="Migrar pro Full"
              destaque={`${num(elegiveis.length)} anúncios`}
              texto={`Passam do corte de ${pct(dados.cortes.conversao_pct)} de conversão e ${dados.cortes.unidades_mes} unidades/mês vendendo por coleta.`}
              cor={theme.palette.success.main}
              onClick={() => setAba(2)}
            />
            <CardAcao
              tag="Perdas no CD"
              destaque={`${num(perdas.reduce((a, p) => a + p.qtd, 0))} unidades`}
              texto="Perdidas, avariadas ou retidas pelo Full. Perda e avaria são ressarcíveis pelo Mercado Livre."
              cor={theme.palette.warning.main}
              onClick={() => setAba(3)}
            />
          </Stack>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <BarraComposicao
              titulo="Situação dos SKUs"
              dica={`Cobertura = disponível ÷ venda média diária dos últimos ${dados.periodo.dias} dias.`}
              partes={[
                { rotulo: 'Zerado no CD', valor: r.ruptura, cor: theme.palette.error.dark },
                { rotulo: `Crítico (<${dados.cortes.critico_dias}d)`, valor: r.critico, cor: theme.palette.error.main },
                { rotulo: `Repor (${dados.cortes.critico_dias}–${dados.cortes.repor_dias}d)`, valor: r.repor, cor: theme.palette.warning.main },
                { rotulo: `Saudável (${dados.cortes.repor_dias}–${dados.cortes.excesso_dias}d)`, valor: r.ok, cor: theme.palette.success.main },
                { rotulo: `Excesso (>${dados.cortes.excesso_dias}d)`, valor: r.excesso, cor: theme.palette.secondary.main },
                { rotulo: 'Sem giro', valor: r.encalhado + r.zerado_sem_giro, cor: theme.palette.text.disabled },
              ]}
            />
            <BarraComposicao
              titulo="Onde está o estoque"
              dica="Unidades que a Fiesta já entregou ao Full, por situação no centro de distribuição."
              partes={[
                { rotulo: 'Disponível', valor: r.disp, cor: theme.palette.success.main },
                { rotulo: 'A caminho', valor: r.transito, cor: theme.palette.info.main },
                {
                  rotulo: 'Perdido ou retido',
                  valor: Math.max(0, r.unidades_cd - r.disp - r.transito),
                  cor: theme.palette.error.main,
                },
              ]}
            />
            <BarraComposicao
              titulo="Quando enviar (unidades)"
              dica={`Unidades a enviar por urgência (inclui SKUs saudáveis com "programar", que ficam em Todos os SKUs). A posição (apto + a caminho) é comparada com o trânsito de ~${dados.lead_dias ?? 7} dias até o CD liberar. Não é a sugestão do ML — ela não existe na API.`}
              partes={[
                { rotulo: 'Esta semana', valor: r.urgencia.esta_semana.un, cor: theme.palette.error.main },
                { rotulo: 'Próxima semana', valor: r.urgencia.proxima_semana.un, cor: theme.palette.warning.main },
                { rotulo: 'Em duas semanas', valor: r.urgencia.duas_semanas.un, cor: theme.palette.info.main },
                { rotulo: 'Programar (em Todos os SKUs)', valor: r.urgencia.programar.un, cor: theme.palette.text.disabled },
              ]}
            />
          </Stack>

          <Card variant="outlined">
            <Tabs value={aba} onChange={(_, v) => setAba(v)} variant="scrollable" scrollButtons="auto">
              <Tab label={`Repor agora (${repor.length})`} />
              <Tab label={`Encalhado (${parado.length})`} />
              <Tab label={`Migrar pro Full (${elegiveis.length})`} />
              <Tab label={`Perdas no CD (${perdas.length})`} />
              <Tab label={`Todos os SKUs (${skus.length})`} />
              <Tab label="Como foi calculado" />
            </Tabs>
            <CardContent>
              {aba === 0 && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '78ch' }}>
                    SKUs com menos de {dados.cortes.repor_dias} dias de cobertura no ritmo dos
                    últimos {dados.periodo.dias} dias. <b>Posição</b> = apto para venda + o que já
                    está a caminho — e é <b>dela</b> que sai a cobertura e a situação.{' '}
                    <b>Enviar</b> = quanto falta em cima dessa posição para fechar{' '}
                    {dados.alvo_dias} dias de venda.
                  </Typography>
                  {emRota.length > 0 && (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      <b>{emRota.length} destes SKUs já têm remessa em rota</b> ({num(unidadesEmRota)}{' '}
                      unidades que saíram e o CD ainda não liberou). O <b>Enviar</b> de cada linha já
                      desconta isso — a coluna mostra <b>só o que falta além do que já vem</b>, com o
                      selo "já vêm N" ao lado. {semFaltar > 0 && (
                        <>
                          {semFaltar === 1 ? 'Um deles' : `${semFaltar} deles`} está com{' '}
                          <b>"não precisa"</b>: a remessa em rota já resolve.
                        </>
                      )}
                    </Alert>
                  )}
                  <TabelaOrdenavel
                    colunas={colsRepor}
                    linhas={repor}
                    vazio="Nenhum SKU abaixo do corte de cobertura neste filtro."
                  />
                  {dados.saiu_do_full && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Zerou e saiu do Full ({saiu.length})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '78ch' }}>
                        Venderam pelo CD nos últimos {dados.periodo.dias} dias, zeraram e hoje estão
                        fora do Full (vendendo por coleta). Não aparecem no retrato do CD, por isso
                        ficam aqui e não na tabela acima. <b>Vendeu pelo CD</b> é a demanda que o
                        Full atendia; <b>Enviar</b> fecha {dados.alvo_dias} dias nesse ritmo.
                      </Typography>
                      <TabelaOrdenavel
                        colunas={colsSaiu}
                        linhas={saiu}
                        vazio="Nenhum SKU saiu do Full neste filtro."
                      />
                    </Box>
                  )}
                </>
              )}
              {aba === 1 && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '78ch' }}>
                    Estoque no Full que não vendeu nenhuma unidade em {dados.periodo.dias} dias, ou
                    que tem mais de {dados.cortes.excesso_dias} dias de cobertura. Cada unidade aqui
                    paga armazenagem e caminha para a taxa de estoque antigo.
                  </Typography>
                  <TabelaOrdenavel
                    colunas={colsParado}
                    linhas={parado}
                    vazio="Nenhum SKU encalhado neste filtro."
                  />
                </>
              )}
              {aba === 2 && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '78ch' }}>
                    Anúncios em coleta que passam dos dois cortes:{' '}
                    <b>conversão acima de {pct(dados.cortes.conversao_pct)}</b> e{' '}
                    <b>mais de {dados.cortes.unidades_mes} unidades no mês</b>. Conversão = unidades
                    vendidas ÷ visitas do anúncio nos mesmos {dados.periodo.dias} dias.
                  </Typography>
                  <TabelaOrdenavel
                    colunas={colsMigrar}
                    linhas={elegiveis}
                    vazio="Nenhum anúncio de coleta passa dos dois cortes neste filtro."
                  />
                  <Typography variant="subtitle2" sx={{ mt: 3 }}>
                    Quase lá ({quaseLa.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '78ch' }}>
                    Vendem mais de {dados.cortes.unidades_mes} unidades por mês mas convertem abaixo
                    de {pct(dados.cortes.conversao_pct)}. Tráfego não falta — antes de mandar pro
                    Full, vale mexer em preço, foto ou título.
                  </Typography>
                  <TabelaOrdenavel colunas={colsMigrar} linhas={quaseLa} vazio="Nada por aqui neste filtro." />
                </>
              )}
              {aba === 3 && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '78ch' }}>
                    Unidades que entregamos ao Full e não estão disponíveis para venda: perdidas,
                    avariadas, retiradas ou retidas. Perda e avaria são ressarcíveis pelo Mercado
                    Livre.
                  </Typography>
                  <TabelaOrdenavel
                    colunas={colsPerdas}
                    linhas={perdas}
                    vazio="Nenhuma perda registrada neste filtro."
                  />
                </>
              )}
              {aba === 4 && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '78ch' }}>
                    Um SKU por linha (o <Box component="span" sx={{ fontFamily: 'monospace' }}>inventory_id</Box>{' '}
                    do Full). Anúncios de catálogo que dividem o mesmo estoque aparecem juntos.
                  </Typography>
                  <TabelaOrdenavel colunas={colsTodos} linhas={skus} vazio="Nada encontrado." ordemInicial={7} />
                </>
              )}
              {aba === 5 && <ComoFoiCalculado dados={dados} />}
            </CardContent>
          </Card>

          <PropostaIaCard
            title="Leitura do Estoque Full"
            caption="Verdade = KPIs e tabelas (reposição, encalhado, migração). Proposta = leitura da IA — revise antes de enviar ou migrar."
            state={leituraIa}
            onRetry={() => void carregarLeitura()}
          />

          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            Vendas e margem de {dados.periodo.de} a {dados.periodo.ate} ({dados.periodo.dias} dias,
            corte por {dados.periodo.campo}). &quot;Vendeu 30d&quot; conta só o que saiu do CD
            ({dados.periodo.vendas_full ?? 'API do ML'}); Flex e coleta ficam à parte. Estoque, vendas,
            visitas e status dos anúncios lidos na API do Mercado Livre em {dados.gerado_em}. Contas:{' '}
            {Object.values(dados.contas).map((c) => `${c.nome} (${c.cidade})`).join(' e ')}. Alvo de
            reposição: {dados.alvo_dias} dias.
            {dados.demanda_nao_atribuida.length > 0 &&
              ` Demanda não atribuída a um SKU: ${dados.demanda_nao_atribuida
                .map((x) => `${x.mlb} (${x.un30} un)`)
                .join(', ')}.`}
          </Typography>
        </Stack>
      )}
    </DomainPageShell>
  )
}

// ---------------------------------------------------------------- procedência

/**
 * A faixa que diz, sem rodeio, de quando é cada metade do painel.
 *
 * As duas metades têm relógios diferentes e misturá-las seria mentir: margem e demanda saem do
 * Mongo a cada carga da tela (segundos), enquanto o estoque no CD, as vendas que saíram do CD e
 * as visitas vêm de uma coleta na API do ML que leva de 2 a 12 minutos e roda pelo cron (03h e
 * 13h) ou por este botão. Decisão da Mariana em 29/09/2026: em vez de encurtar o cron, o botão
 * fica em destaque — ela atualiza na hora de montar o envio pro Full.
 */
function ProcedenciaFull({
  estado,
  motor,
  pedindo,
  onAtualizar,
}: {
  estado: DadosEstoqueFull
  motor: StatusMotor | null
  pedindo: boolean
  onAtualizar: () => void
}) {
  const coletando = Boolean(motor?.coletando || estado.coletando)
  const haMin = estado.estoqueHaMin ?? motor?.estoque_coletado_ha_min ?? null
  const velho = haMin != null && haMin > 24 * 60

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          <Chip
            size="small"
            color={estado.fonte === 'servico' ? 'success' : 'warning'}
            label={estado.fonte === 'servico' ? 'Motor no ar' : 'Arquivo publicado'}
          />
          {estado.fonte === 'servico' && (
            <Typography variant="caption" color="text.secondary">
              Receita e margem: <b>consultadas agora</b>
              {estado.consultaMongo ? ` (${estado.consultaMongo})` : ''}
            </Typography>
          )}
          <Typography variant="caption" color={velho ? 'warning.main' : 'text.secondary'}>
            Estoque no CD, vendas que saíram do CD e visitas: leitura de <b>{estado.estoqueEm ?? 'data não informada'}</b>
            {haMin != null && ` · há ${haMin < 90 ? `${haMin} min` : `${Math.round(haMin / 60)} h`}`}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {coletando ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                lendo estoque e vendas na API do Mercado Livre… (costuma levar de 2 a 12 min)
              </Typography>
            </Stack>
          ) : (
            <Tooltip title="Lê agora na API do Mercado Livre o estoque no CD, as vendas que saíram do CD e as visitas. Use antes de montar o envio pro Full: entre uma coleta e outra o retrato é o da última leitura.">
              <span>
                <Button variant="contained" onClick={onAtualizar} disabled={pedindo || !motor}>
                  {pedindo ? 'pedindo…' : velho ? 'Atualizar agora (retrato velho)' : 'Atualizar agora'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
        {motor?.ultima_coleta?.em_sp && !coletando && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            Última coleta completa: {motor.ultima_coleta.em_sp}
            {motor.ultima_coleta.duracao_segundos != null &&
              ` · ${Math.round(motor.ultima_coleta.duracao_segundos / 60)} min`}
            {motor.ultima_coleta.ok === false && ` · falhou: ${motor.ultima_coleta.erro ?? 'sem motivo'}`}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------- método

/**
 * A aba que diz de onde vem cada número e no que confiar menos. Ela não é enfeite: é o que
 * separa dado real de cálculo nosso, e é obrigatória pelas regras da casa.
 */
function ComoFoiCalculado({ dados }: { dados: import('./estoqueFull/types').EstoqueFullData }) {
  const m = dados.meta
  return (
    <Stack spacing={2} sx={{ maxWidth: '82ch' }}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          De onde vem cada número
        </Typography>
        <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
          <li>
            <b>Estoque no CD</b> — API do Mercado Livre,{' '}
            <code>/inventories/{'{id}'}/stock/fulfillment</code>, lido SKU a SKU. Disponível, a
            caminho e indisponível vêm separados pela própria API.
          </li>
          <li>
            <b>A caminho</b> — unidades já despachadas que o CD ainda não liberou pra venda
            (<code>not_available_detail.status = "transfer"</code>). Elas já são suas e já entram na
            conta da reposição — por isso a coluna <b>Enviar</b> nunca manda repetir o que está em
            rota.
          </li>
          <li>
            <b>Vendas de {dados.periodo.dias} dias</b> — pedidos pagos lidos em{' '}
            <code>/orders/search</code> da API do ML, contando <b>só os itens cujo envio saiu do CD</b>{' '}
            (<code>/shipments/{'{id}'}.logistic_type = "fulfillment"</code>). É assim que o painel do
            vendedor conta. O que o mesmo anúncio vendeu por Flex ou coleta aparece como
            &quot;fora do CD&quot; e não entra na cobertura. Não usamos <code>sold_quantity</code>, que
            é histórico acumulado do anúncio. Os candidatos a migrar (anúncios fora do Full) usam{' '}
            <code>mongodb_api.orders</code>, porque neles a pergunta é quanto vendem por qualquer
            logística.
          </li>
          <li>
            <b>Quando enviar</b> — a sugestão de reposição do Mercado Livre não existe na API pública
            (dez rotas testadas em 02/09/2026, todas 404); ela só vive em Estoque → Reposição. A tag
            sai da cobertura da posição descontado o trânsito de ~{dados.lead_dias ?? 7} dias até o CD
            liberar: <b>esta semana</b> se a posição dura menos de {(dados.lead_dias ?? 7) + 8} dias
            (o mesmo corte de &quot;crítico&quot;), <b>próxima semana</b> menos de{' '}
            {(dados.lead_dias ?? 7) + 15}, <b>em duas semanas</b> menos de {(dados.lead_dias ?? 7) + 22},{' '}
            <b>programar</b> quando há o que enviar mas com folga.
          </li>
          <li>
            <b>MC oficial (notas fiscais)</b> — campo <code>calculated_margin</code> de{' '}
            <code>financial.margens</code> com <code>type:"pedido"</code>, já líquido de CMV,
            comissão, envio, impostos e descontos. MC% é ponderada pela receita, nunca média de
            percentuais.
          </li>
          <li>
            <b>MC ajustada (com créditos)</b> — a que o painel destaca. É a oficial (corrigida
            do DIFAL fantasma devolvido, quando há) mais o ajuste de PIS/COFINS efetivo (PIS/COFINS
            da nota menos o efetivo, com crédito sobre taxa e frete) e, na conta da Extrema, o
            regime de MG (ICMS economizado − crédito de ICMS perdido na transferência SP→MG). As
            parcelas vêm prontas da coleção <code>margens_ajustadas</code>, calculada pedido a
            pedido pelo serviço <code>margem-ajustada</code> (skill <code>margem-ajustada-fiesta</code>);
            passe o mouse em qualquer margem para ver as parcelas. As duas andam sempre juntas: a
            oficial vale para o fiscal, a ajustada para decidir.
            {m.margem_ajustada?.calculada_em
              ? ` Calculada em ${horaBrasilia(m.margem_ajustada.calculada_em) ?? m.margem_ajustada.calculada_em}${m.margem_ajustada.versao ? ` · versão ${m.margem_ajustada.versao}` : ''}.`
              : ''}
            {m.margem_ajustada?.ped_sem_ajustada
              ? ` ${num(m.margem_ajustada.ped_sem_ajustada)} pedidos dos anúncios do painel ainda não estão na coleção e ficaram fora da ajustada.`
              : ''}
            {m.margem_ajustada?.ped_cmv0_fora_ajustada
              ? ` ${num(m.margem_ajustada.ped_cmv0_fora_ajustada)} pedidos com CMV zero ficaram fora das duas margens (oficial e ajustada), pela mesma regra.`
              : ''}
            {m.margem_ajustada?.marcas && Object.keys(m.margem_ajustada.marcas).length > 0
              ? ` Tratados por falta de dado (marcas da coleção, nº de pedidos): ${Object.entries(m.margem_ajustada.marcas)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, n]) => `${ROTULO_MARCA[k] ?? k} ${num(n)}`)
                  .join(' · ')}.`
              : ''}
            {m.margem_ajustada?.aviso ? ` ${m.margem_ajustada.aviso}: só a MC oficial está valendo.` : ''}
          </li>
          <li>
            <b>Visitas</b> — <code>/items/{'{id}'}/visits/time_window</code>, {dados.periodo.dias}{' '}
            dias, um anúncio por chamada.
          </li>
        </Typography>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          O que confiar menos
        </Typography>
        <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
          <li>
            <b>A MC está superestimada.</b> {num(m.frete0)} dos {num(m.ped_mc)} pedidos do período
            ({pct(m.frete0_pct)}) estão com custo de envio zerado na base. Parte é falha de
            ingestão confirmada em 26/08/2026; parte é envio já embutido na tarifa de venda.
            Enquanto a origem não for gravada, não dá para separar os dois.
          </li>
          <li>
            <b>{num(m.cmv0_ped)} pedidos ficaram fora do cálculo de margem</b> por estarem com CMV
            zero ({brl(m.cmv0_rec)} de receita). Não existe produto com custo zero — enquanto o
            custo não for cadastrado em <code>financial.cmvs</code>, esses pedidos não entram em
            nenhum indicador.
          </li>
          <li>
            <b>{num(m.mc_por_anuncio)} SKUs estão com a margem no nível do anúncio</b>, não do SKU:
            são variações de um mesmo anúncio, e <code>margens</code> guarda o total do pedido, não
            o valor por variação. A tela mostra "—" nesses casos em vez de ratear.
          </li>
          <li>
            <b>{num(m.sem_cmv)} SKUs estão sem custo cadastrado</b> — o custo parado deles não entra
            no total de custo imobilizado.
          </li>
          <li>
            <b>Visitas têm atraso de até 48 horas</b> e são contadas no anúncio pai, nunca por
            variação. A conversão dos últimos dois dias sai subestimada.
          </li>
          <li>
            <b>Conversão com poucas visitas é ruído.</b> Um anúncio com 54 visitas e 4 pedidos
            aparece com 61% de conversão — isso não se sustenta em volume. A coluna "Antes de
            enviar" marca esses casos.
          </li>
        </Typography>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          As regras da recomendação
        </Typography>
        <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
          <li>
            <b>Cobertura</b> = posição ÷ (vendas de {dados.periodo.dias} dias ÷{' '}
            {dados.periodo.dias}), sendo <b>posição = apto para venda + a caminho</b>. Conta o que
            já está em rota desde 25/09/2026: sem isso, a lista de reposição pedia de novo o que já
            tinha sido pedido. Quem está com zero apto agora leva a marca <b>"parado até chegar"</b>{' '}
            — a situação já considera a remessa, mas o anúncio só volta a vender quando ela entrar.
          </li>
          <li>
            <b>Posição</b> = disponível + a caminho. É o que você terá no CD quando tudo que já saiu
            for recebido.
          </li>
          <li>
            <b>Enviar</b> = {dados.alvo_dias} dias de venda média − posição, nunca negativo.
          </li>
          <li>
            <b>Zerado no CD</b> sem estoque e com venda no período · <b>Crítico</b> abaixo de{' '}
            {dados.cortes.critico_dias} dias · <b>Repor</b> entre {dados.cortes.critico_dias} e{' '}
            {dados.cortes.repor_dias} · <b>Saudável</b> entre {dados.cortes.repor_dias} e{' '}
            {dados.cortes.excesso_dias} · <b>Excesso</b> acima de {dados.cortes.excesso_dias} ·{' '}
            <b>Sem giro</b> quando não houve nenhuma venda no período.
          </li>
          <li>
            <b>Migrar pro Full</b> = anúncio fora do Full com conversão acima de{' '}
            {pct(dados.cortes.conversao_pct)} e mais de {dados.cortes.unidades_mes} unidades
            vendidas no mês.
          </li>
          <li>
            Tudo aqui é <b>sugestão</b>, nunca decisão tomada. O painel não retira nem envia nada
            sozinho.
          </li>
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary">
        Coleta da API em {m.coleta_api} · {num(m.anuncios_full)} anúncios no Full nas duas contas.
      </Typography>
    </Stack>
  )
}
